import {Linking} from 'react-native';
import { isWorkdayReminderPayload, requestWorkdayReminder } from './workdayBus';
import {DRIVER_EVENT_KEYS, buildDriverPushPayload, isDriverPushData} from './driverPushPayload';
import {resolveFollowUpTarget} from './followUpTarget';
import {isCollabEvent} from './mailCollabUtils';
import {chatOpenParams, isThreadChatEvent} from './mailThreadChat';

const ORDER_PATH_REGEX = /^\/orders\/([^/?#]+)/i;
const WORKSHEET_PATH_REGEX = /^\/worksheets\/([^/?#]+)/i;
const MAIL_PATH_REGEX = /^\/mail\/([^/?#]+)/i;
const NOTIFICATIONS_PATH_REGEX = /^\/(notifications|alerts)$/i;
const SETTINGS_NOTIFICATIONS_PATH_REGEX = /^\/settings(?:\/notifications)?$/i;

const SCREEN_ALIASES = {
    orders: 'OrdersScreen',
    orderdetails: 'OrderDetailsScreen',
    notifications: 'NotificationsScreen',
    settings: 'SettingsScreen',
    worksheetdetail: 'WorkSheetDetailScreen',
    worksheets: 'WorkSheetListScreen',
    mailthreadscreen: 'MailThreadScreen',
    mailhomescreen: 'MailHomeScreen',
    mail: 'MailHomeScreen',
};

const normalizeScreenName = (screenName = '') => screenName.replace(/[^a-z]/gi, '').toLowerCase();

/**
 * First non-empty value among the given keys, coerced to a trimmed string.
 * FCM `data` values always arrive as strings and different producers spell the
 * keys differently (camelCase vs all-lowercase), so every lookup is defensive.
 *
 * @param {Object} source Payload object (FCM/local notification `data`).
 * @param {...string} keys Candidate keys, most specific first.
 * @returns {string} The first non-empty value, or ''.
 */
const pickString = (source, ...keys) => {
    if (!source || typeof source !== 'object') {
        return '';
    }
    for (const key of keys) {
        const value = source[key];
        if (value !== undefined && value !== null && String(value).trim() !== '') {
            return String(value).trim();
        }
    }

    return '';
};

/**
 * Optional mail "hint" params carried straight from the push payload into the
 * reader's route params, so MailThreadScreen can paint the sender, subject and
 * a plain-text preview on its FIRST frame instead of waiting for the network.
 *
 * Purely additive — every field is omitted when the payload does not carry it,
 * so non-mail branches and older payloads behave exactly as before. Both the
 * plain (`subject`) and prefixed (`pushSubject`) names are emitted so the reader
 * can consume either without a lock-step deploy.
 *
 * @param {Object} data Notification `data` payload.
 * @param {{title?: string, body?: string}} [notification] Optional FCM `notification` block.
 * @returns {Object} Param fragment to spread into a mail target's params.
 */
const buildMailHintParams = (data, notification) => {
    const subject = pickString(data, 'subject', 'mailSubject', 'mailsubject');
    const fromName = pickString(data, 'fromName', 'fromname', 'senderName', 'sendername', 'from');
    const fromEmail = pickString(data, 'fromEmail', 'fromemail', 'senderEmail', 'senderemail');
    // The `notification.body` fallback is preview-only on purpose: the sender and
    // the subject are never GUESSED from the banner text (its format is
    // backend-owned and has changed before), only read from explicit data keys.
    const preview = pickString(data, 'preview', 'previewText', 'previewtext', 'snippet')
        || pickString(notification, 'body');
    const receivedAt = pickString(data, 'receivedAt', 'receivedat', 'sentAt', 'sentat');

    return {
        ...(subject ? { subject, pushSubject: subject } : {}),
        ...(fromName ? { fromName, pushFromName: fromName } : {}),
        ...(fromEmail ? { fromEmail, pushFromEmail: fromEmail } : {}),
        ...(preview ? { preview, pushPreview: preview } : {}),
        ...(receivedAt ? { receivedAt, pushReceivedAt: receivedAt } : {}),
    };
};

/**
 * Adapt a NOTIFICATION INBOX row into the push-payload shape
 * {@link resolveNotificationNavigationTarget} expects.
 *
 * Spreads the row's `metadata` (which carries the catalog `eventKey` and any
 * `clickAction` / `screen` / `threadKey` / `mailMessageId` routing keys) and maps
 * `relatedModel`/`relatedId` onto the resolver's entity id keys — only for model
 * names the resolver actually routes, so an unrelated model never mis-navigates to
 * an order.
 *
 * Shared by the inbox list and the notification detail screen so both resolve the
 * SAME destination; they must never drift apart.
 *
 * @param {Object} notification Notification document from the inbox API.
 * @returns {{data: Object}} Payload for `resolveNotificationNavigationTarget`.
 */
export function buildNotificationNavigationData(notification) {
    const metadata = notification && typeof notification.metadata === 'object' && !Array.isArray(notification.metadata)
        ? notification.metadata
        : {};
    const relatedModel = String(notification?.relatedModel || '').toLowerCase();
    const relatedId = notification?.relatedId ? String(notification.relatedId) : '';

    return {
        data: {
            ...metadata,
            ...(notification?.type && !isWorkdayReminderPayload(metadata) ? { type: notification.type } : {}),
            ...(relatedId && relatedModel.includes('order') && !relatedModel.includes('work') ? { orderId: relatedId } : {}),
            ...(relatedId && (relatedModel === 'task' || relatedModel.includes('worksheet') || relatedModel.includes('workorder'))
                ? { worksheetId: relatedId }
                : {}),
            ...(relatedId && relatedModel.includes('mail') ? { mailMessageId: relatedId } : {}),
        },
    };
}

/**
 * Whether an inbox row is the workday end-of-day reminder (metadata.type OR the `happyapp://workday/reminder` deep link).
 * Such a row has no screen: callers must open the in-app reminder modal via the workday bus (never `Linking.openURL`).
 *
 * @param {Object} notification Notification document from the inbox API.
 * @returns {boolean} true for the workday reminder.
 */
export function isWorkdayReminderNotification(notification) {
    return isWorkdayReminderPayload(buildNotificationNavigationData(notification).data);
}

/**
 * Team Chat push / inbox targets. Payloads (backend contract):
 *   `{type:'chat', channelId, messageId, restaurantId}` (eventKeys chat.message.*)
 *   `{type:'chat-meeting', meetingId, channelId, restaurantId}` (chat.meeting.*)
 * A meeting tap opens the meeting LOBBY (never auto-joins — joining spends
 * credits); without a meetingId it falls back to the channel.
 *
 * @param {Object} data Notification `data` payload.
 * @returns {{name: string, params: Object}|null}
 */
const resolveTeamChatTarget = (data) => {
    const type = String(data.type || '').toLowerCase();
    const eventKey = String(data.eventKey || data.eventkey || '').toLowerCase();
    const isMeeting = type === 'chat-meeting' || eventKey.startsWith('chat.meeting');
    const isMessage = type === 'chat' || eventKey.startsWith('chat.message');
    if (!isMeeting && !isMessage) {
        return null;
    }
    const channelId = pickString(data, 'channelId', 'channelid');
    const restaurantId = pickString(data, 'restaurantId', 'restaurantid');
    if (isMeeting) {
        const meetingId = pickString(data, 'meetingId', 'meetingid');
        if (meetingId) {
            return {
                name: 'ChatMeetingScreen',
                params: { meetingId, ...(channelId ? { channelId } : {}), ...(restaurantId ? { restaurantId } : {}) },
            };
        }
    }
    if (channelId) {
        const messageId = pickString(data, 'messageId', 'messageid');

        return {
            name: 'ChatChannelScreen',
            params: { channelId, ...(messageId ? { messageId } : {}), ...(restaurantId ? { restaurantId } : {}) },
        };
    }

    return { name: 'ChatHomeScreen', params: {} };
};

/**
 * Driver / Courier push + inbox targets. Payload (backend contract):
 *   `{type:'driver', eventKey, routePlanId?, stopId?, warehouseId?, changeRequestId?,
 *     status?, changeSummary? (JSON string), deepLink?}`
 *   - driver.route.assigned / .updated → DriverRouteDetailScreen {routeId, changeSummary}
 *   - driver.route.unassigned / .cancelled → driver Útvonal tab with `notice: 'revoked'`
 *   - driver.stock.loaded → driver Raktár tab
 *   - driver.changeRequest.resolved → DriverChangeRequestsScreen {highlightId, status}
 * Tabs live inside DriverHomeScreen, so they are addressed as a nested target
 * (`navigate('DriverHomeScreen', {screen, params})`) which works from the stack
 * root on cold start, background and foreground alike. A malformed
 * `changeSummary` is dropped (the screen then just refetches).
 *
 * @param {Object} data Notification `data` payload.
 * @returns {{name: string, params: Object}|null}
 */
const resolveDriverTarget = (data) => {
    if (!isDriverPushData(data)) {
        return null;
    }
    const payload = buildDriverPushPayload(data);
    const tab = (screen, params = {}) => ({name: 'DriverHomeScreen', params: {screen, params}});

    switch (payload.eventKey) {
        case DRIVER_EVENT_KEYS.routeAssigned:
        case DRIVER_EVENT_KEYS.routeUpdated:
            if (!payload.routePlanId) {
                return tab('DriverRouteTab');
            }

            return {
                name: 'DriverRouteDetailScreen',
                params: {
                    routeId: payload.routePlanId,
                    ...(payload.changeSummary ? {changeSummary: payload.changeSummary} : {}),
                },
            };
        case DRIVER_EVENT_KEYS.routeUnassigned:
        case DRIVER_EVENT_KEYS.routeCancelled:
            return tab('DriverRouteTab', {notice: 'revoked'});
        case DRIVER_EVENT_KEYS.stockLoaded:
            return tab('DriverStockTab', {refresh: true});
        case DRIVER_EVENT_KEYS.changeRequestResolved:
            return {
                name: 'DriverChangeRequestsScreen',
                params: {
                    ...(payload.changeRequestId ? {highlightId: payload.changeRequestId} : {}),
                    ...(payload.status ? {status: payload.status} : {}),
                },
            };
        default:
            return {name: 'DriverHomeScreen', params: {}};
    }
};

export function resolveNotificationNavigationTarget(messageOrData) {
    const data = messageOrData?.data || messageOrData || {};
    const notification = messageOrData?.notification || null;
    // Team Chat first: its payload carries a `messageId` that the mail branch
    // below would otherwise never see, but must not be read as anything else.
    const teamChatTarget = resolveTeamChatTarget(data);
    if (teamChatTarget) {
        return teamChatTarget;
    }
    // CRM follow-ups / lead nudges (crm.followup.*, crm.lead.*): digests -> Teendők, single events focus the row,
    // lead events open the lead.
    const followUpTarget = resolveFollowUpTarget(data);
    if (followUpTarget) {
        return followUpTarget.kind === 'lead'
            ? {name: 'ManagementDetailScreen', params: followUpTarget.params}
            : {name: 'SalesFollowUpsScreen', params: followUpTarget.params};
    }
    const driverTarget = resolveDriverTarget(data);
    if (driverTarget) {
        return driverTarget;
    }
    const mailHints = buildMailHintParams(data, notification);
    const clickAction = data.clickAction || data.clickaction || data.deepLink || data.deeplink || '';
    const explicitScreen = data.screen || data.targetScreen || '';
    const orderId = data.orderId || data.orderID || data.entityId || data.entityID || '';
    const worksheetId = data.worksheetId || data.taskId || '';
    const mailEventKey = String(data.eventKey || data.eventkey || '').toLowerCase();
    // Older `mail.*` rows (e.g. `mail.opened` read receipts stored as `system`) only carry
    // `messageId`; accept it for mail events so they still open the message they are about.
    const mailMessageId = data.mailMessageId || data.mailmessageid
        || (mailEventKey.startsWith('mail.') ? data.messageId || data.messageid || '' : '');
    const threadKey = data.threadKey || data.threadkey || '';
    const isMailEvent = String(data.type || data.eventKey || '').toLowerCase() === 'mail'
        || mailEventKey.startsWith('mail.');
    const isVoipCallEvent = String(data.eventKey || '').toLowerCase().startsWith('voip.call');

    // Shared-mailbox collaboration (mail.assigned / reassigned / shared_reply / snooze_due): the payload
    // names a conversation + mailbox but carries no message id, so the mail list resolves and opens it
    // (`collabOpen`) instead of the reader opening empty.
    // Thread discussion pushes (mail.thread_comment / mail.thread_mention) also open the discussion sheet.
    const chatHint = chatOpenParams(data);
    // Follow-up reminders and discussion pushes may carry only the thread key (no message id).
    const isThreadKeyOnlyEvent = isThreadChatEvent(data) || mailEventKey === 'mail.snooze_due';

    const collabConversationKey = data.conversationKey || data.conversationkey || '';
    if (isCollabEvent(data) && collabConversationKey && !mailMessageId && !threadKey) {
        return {
            name: 'MailHomeScreen',
            params: {
                collabOpen: {
                    conversationKey: String(collabConversationKey),
                    mailboxAddress: String(data.mailboxAddress || data.mailboxaddress || ''),
                },
            },
        };
    }

    if (clickAction) {
        const orderMatch = clickAction.match(ORDER_PATH_REGEX);
        if (orderMatch?.[1]) {
            return {
                name: 'OrderDetailsScreen',
                params: { orderId: orderMatch[1] },
            };
        }

        const worksheetMatch = clickAction.match(WORKSHEET_PATH_REGEX);
        if (worksheetMatch?.[1]) {
            return {
                name: 'WorkSheetDetailScreen',
                params: { worksheetId: worksheetMatch[1] },
            };
        }

        const mailMatch = clickAction.match(MAIL_PATH_REGEX);
        if (mailMatch?.[1]) {
            return {
                name: 'MailThreadScreen',
                params: { messageId: mailMatch[1], ...(threadKey ? { threadKey } : {}), ...mailHints, ...chatHint },
            };
        }

        if (NOTIFICATIONS_PATH_REGEX.test(clickAction)) {
            return { name: 'NotificationsScreen', params: {} };
        }

        if (SETTINGS_NOTIFICATIONS_PATH_REGEX.test(clickAction)) {
            return { name: 'SettingsScreen', params: {} };
        }

        if (/^(https?:|[a-z][a-z0-9+.-]*:)/i.test(clickAction)) {
            return {
                externalUrl: clickAction,
            };
        }
    }

    if (explicitScreen) {
        const normalizedScreen = normalizeScreenName(explicitScreen);
        const resolvedScreen = SCREEN_ALIASES[normalizedScreen] || explicitScreen;
        // Hints are only attached when the resolved target is actually a mail
        // screen, so an order/worksheet target keeps byte-for-byte identical params.
        const isMailScreen = String(resolvedScreen).startsWith('Mail');
        return {
            name: resolvedScreen,
            params: {
                ...(orderId ? { orderId } : {}),
                ...(worksheetId ? { worksheetId } : {}),
                ...(mailMessageId ? { messageId: mailMessageId } : {}),
                ...(threadKey ? { threadKey } : {}),
                ...(isMailScreen ? mailHints : {}),
                ...(isMailScreen ? chatHint : {}),
            },
        };
    }

    if (isMailEvent || mailMessageId) {
        if (!mailMessageId && threadKey && isThreadKeyOnlyEvent) {
            return { name: 'MailThreadScreen', params: { threadKey, ...mailHints, ...chatHint } };
        }

        return mailMessageId
            ? { name: 'MailThreadScreen', params: { messageId: mailMessageId, ...(threadKey ? { threadKey } : {}), ...mailHints, ...chatHint } }
            : { name: 'MailHomeScreen', params: {} };
    }

    if (isVoipCallEvent) {
        return { name: 'CallHistoryScreen', params: {} };
    }

    if (orderId) {
        return {
            name: 'OrderDetailsScreen',
            params: { orderId },
        };
    }

    if (worksheetId) {
        return {
            name: 'WorkSheetDetailScreen',
            params: { worksheetId },
        };
    }

    return null;
}

export async function navigateFromNotificationMessage(navigation, message) {
    // Workday end-of-day reminder (server push / local notification): no screen to navigate to — open the in-app
    // reminder modal (latched by the bus until the WorkdayProvider is mounted on a cold start).
    if (isWorkdayReminderPayload(message?.data || message)) {
        requestWorkdayReminder();

        return true;
    }

    const target = resolveNotificationNavigationTarget(message);

    if (!target) {
        return false;
    }

    if (target.externalUrl) {
        await Linking.openURL(target.externalUrl);
        return true;
    }

    if (!navigation?.navigate || !target.name) {
        return false;
    }

    navigation.navigate(target.name, target.params || {});
    return true;
}

